import Accordion from '@/components/_atoms/Accordion';
import PrimaryInnerGlowButton from '@/components/buttons/primary';
import Carousel from '@/components/carousels/dots';
import Glyph from '@/components/Glyph';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { CareersPageQuery } from '@/graphql';
import CareersPageQueryDocument from '@/graphql/queries/CareersPage.gql';
import fetchJobPostings, { Department, Jobs } from '@/lib/ashby';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPageProps } from '@/lib/next';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet } from '@/lib/typescript-helpers';
import { ConditionalWrapper } from '@/lib/utils';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  marginX,
  mediaBreakpointDown,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';

// #region - Components

const Hero = styled.header`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  padding-bottom: ${remCalc(56)};

  ${mediaBreakpointUp('tablet')} {
    padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 84)};
    padding-bottom: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktop')} {
    padding-bottom: ${remCalc(100)};
  }

  ${Container} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const HeroHeadline = styled.h1`
  ${customTextSize(38, 46)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-align: center;
  margin-bottom: ${remCalc(32)};

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(80, 88)}

    margin-bottom: ${remCalc(40)};
  }
`;

const HeroText = styled.div`
  ${customTextSize(18, 26)}
  text-align: center;

  max-width: ${remCalc(720)};
  margin: 0 auto;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
  }
`;

const HeroCTA = styled.div`
  margin-top: ${remCalc(60)};
`;

const SectionHeadline = styled.h2`
  ${customTextSize(24, 30)}
  color: ${COLORS.BLACK};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-align: center;
  margin-bottom: ${remCalc(32)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)}
    margin-bottom: ${remCalc(100)};
  }
`;

const SectionHeadlineText = styled.div`
  ${customTextSize(18, 26)}
  text-align: center;
  margin-bottom: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
    margin-bottom: ${remCalc(80)};

    ${SectionHeadline} + & {
      margin-top: ${remCalc(-60)};
    }
  }
`;

const NewsSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(0, 0, 180)};
  }
`;

const NewsHeadline = styled.h2`
  ${customTextSize(32, 38)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.BLACK};
  text-align: center;
  margin-bottom: ${remCalc(32)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)}
    margin-bottom: ${remCalc(40)};
  }
`;

const StyledCarousel = styled(Carousel)`
  ${applyBreakpointNegativeMargins}
`;

const CarouselItem = styled.div`
  padding: ${remCalc(44, 20)};
  background-color: ${COLORS.GRAY.TINT};
  border-radius: ${remCalc(10)};
  box-shadow: 0px 0px 30px rgba(0, 27, 56, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(60, 30)};
  }
`;

const CarouselItemSupertitle = styled.p`
  ${customTextSize(16, 20)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-transform: uppercase;
  color: ${COLORS.GRAY.BASE};
  text-align: center;
  margin-bottom: ${remCalc(10)};
`;

const CarouselItemTitle = styled.p`
  ${customTextSize(16, 20)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 30)}
  }

  a {
    cursor: pointer;
    color: ${COLORS.BLACK};

    &:hover,
    &:active {
      text-decoration: underline;
    }
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-gap: ${remCalc(16)};

  ${mediaBreakpointUp('desktop')} {
    grid-gap: ${remCalc(24)};
  }
`;

const PhotoGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 2fr 1fr 1fr;
  height: ${remCalc(900)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }

  > div {
    position: relative;

    img {
      object-fit: cover;
      object-position: center;
    }

    &:nth-child(1) {
      grid-column: 1 / span 2;

      ${mediaBreakpointUp('desktop')} {
        grid-column: 1;
        grid-row: 1 / span 2;
      }
    }

    &:nth-child(4) {
      grid-column: 1 / span 2;

      ${mediaBreakpointUp('desktop')} {
        grid-column: 2 / span 2;
        grid-row: 2;
      }
    }
  }
`;

const ValuesSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${Container} {
    max-width: ${remCalc(920)};
  }

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(168, 0, 180)};
  }
`;

const BenefitsSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(168, 0, 180)};
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: auto;
  grid-gap: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const BenefitCarouselItem = styled.div`
  padding: ${remCalc(0, 20)};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${mediaBreakpointUp('tablet')} {
    padding: 0;
  }
`;

const BenefitCarouselItemIcon = styled.div`
  padding: ${remCalc(8)};
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(8)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BLACK};
`;

const BenefitCarouselItemTitle = styled.p`
  ${customTextSize(24, 30)}
  text-align: center;
  margin-bottom: ${remCalc(24)};

  ${mediaBreakpointUp('tablet')} {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 30)}
  }
`;

const BenefitCarouselItemText = styled.p`
  ${customTextSize(16, 26)}
  text-align: center;
`;

const CultureSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(168, 0, 180)};
  }
`;

const CulturePhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 3fr 1fr 1fr;
  grid-gap: ${remCalc(16)};
  height: ${remCalc(700)};

  ${mediaBreakpointUp('desktop')} {
    height: ${remCalc(650)};
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    grid-gap: ${remCalc(16, 30)};
  }

  > div {
    display: flex;
    flex-direction: column;

    /* Image */
    > div {
      position: relative;
      border-radius: ${remCalc(10)};
      overflow: hidden;
      height: 100%;

      img {
        object-fit: cover;
        object-position: center;
      }

      ${mediaBreakpointUp('desktop')} {
        height: calc(100% - ${remCalc(50)});
      }
    }

    /* Caption */
    > p {
      display: none;
      ${customTextSize(15, 24)}
      text-align: center;
      margin: auto;
      width: 100%;

      ${mediaBreakpointUp('desktop')} {
        display: block;
      }
    }

    &:nth-child(1) {
      grid-column: 1 / span 2;

      ${mediaBreakpointUp('desktop')} {
        grid-column: 1;
        grid-row: 1 / span 2;
      }
    }
  }
`;

const CultureQuote = styled.div`
  margin-top: ${remCalc(90)};
  max-width: ${remCalc(1000)};
  ${marginX('auto')}

  ${mediaBreakpointUp('tablet')} {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  blockquote {
    ${customTextSize(18, 24)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.GREEN.LOWLIGHT};

    ${mediaBreakpointOnly('mobile')} {
      text-align: center;
    }

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(27, 32)}
    }

    p {
      &::before {
        content: '“';
      }

      &::after {
        content: '”';
      }
    }

    footer {
      margin-top: ${remCalc(24)};
      font-weight: ${FONT_WEIGHTS.NORMAL};
      ${customTextSize(16, 24)}

      ${mediaBreakpointOnly('mobile')} {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
  }
`;

const CultureQuoteImageWrapper = styled.div`
  width: 100%;
  max-width: ${remCalc(140)};
  margin: 0 0 ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
    flex-shrink: 0;
    width: 30%;
    margin-right: ${remCalc(40)};
  }

  ${mediaBreakpointUp('desktop')} {
    width: ${remCalc(270)};
    margin-right: ${remCalc(80)};
  }
`;

const CultureQuoteImage = styled(Image)`
  border-radius: 100%;
  overflow: hidden;
  display: block;
`;

const LocationsSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(168, 0, 180)};
  }
`;

const LocationsGrid = styled.div`
  max-width: ${remCalc(920)};
  ${marginX('auto')}

  ${mediaBreakpointUp('tablet')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-column-gap: ${remCalc(60)};
    align-items: flex-start;
  }
`;

const LocationImageWrapper = styled.div`
  position: relative;
  display: none;

  border-radius: ${remCalc(10)};
  overflow: hidden;

  img {
    object-fit: cover;
    object-position: center;
  }

  ${mediaBreakpointUp('tablet')} {
    display: block;
    min-height: ${remCalc(540)};
  }
`;

const LocationsAccordionImage = styled.div`
  position: relative;
  height: ${remCalc(230)};
  margin-bottom: ${remCalc(20)};

  border-radius: ${remCalc(10)};
  overflow: hidden;

  img {
    object-fit: cover;
    object-position: center;
  }

  ${mediaBreakpointUp('tablet')} {
    display: none;
  }
`;

const OpenPositionsSection = styled.section`
  padding: ${remCalc(74, 0, 80)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(168, 0, 180)};
  }
`;

const OpenPositionGroup = styled.div`
  border-top: 1px solid ${hexToRGBA(COLORS.GRAY.BASE, 0.35)};

  ${mediaBreakpointDown('tablet')} {
    ${applyBreakpointNegativeMargins}

    &:last-of-type {
      border-bottom: 1px solid ${hexToRGBA(COLORS.GRAY.BASE, 0.35)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    border-top: none;

    & + & {
      margin-top: ${remCalc(50)};
    }
  }
`;

const OpenPositionDepartment = styled.h3`
  ${customTextSize(18, 18)}
  color: ${COLORS.BLACK};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  ${paddingY(24)}

  ${mediaBreakpointDown('tablet')} {
    position: sticky;
    top: ${remCalc(PRIMARY_NAV_HEIGHT - 1)};

    ${applyBreakpointPadding}
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(26, 26)}
    margin-bottom: ${remCalc(16)};
    padding: 0;
    background-color: transparent;
  }
`;

const OpenPositionItem = styled.li`
  ${customTextSize(14, 20)}
  border-top: 1px solid ${hexToRGBA(COLORS.GRAY.BASE, 0.35)};

  a {
    display: block;
  }

  ${mediaBreakpointDown('tablet')} {
    a {
      ${applyBreakpointPadding}
      ${paddingY(12)}
    }
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 24)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(20, 28)}

    &:last-of-type {
      border-bottom: 1px solid ${hexToRGBA(COLORS.GRAY.BASE, 0.35)};
    }

    a {
      ${paddingY(10)}
      display: flex;
      justify-content: space-between;
    }
  }
`;

const LargeSelectField = styled.select`
  appearance: none;
  padding: ${remCalc(8, 32, 8, 12)};
  background-color: ${COLORS.GRAY.TINT};
  background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0ibm9uZSIgdmlld0JveD0iMCAwIDE2IDE2Ij48cGF0aCBmaWxsPSIjMTkxOTFBIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMi4zNTM2IDYuMzEyOTVDMTIuNTQ4OCA2LjUwODIxIDEyLjU0ODggNi44MjQ4IDEyLjM1MzYgNy4wMjAwNkw4LjM1MzU1IDExLjAyMDFDOC4yNTk3OSAxMS4xMTM4IDguMTMyNjEgMTEuMTY2NSA4IDExLjE2NjVDNy44NjczOSAxMS4xNjY1IDcuNzQwMjEgMTEuMTEzOCA3LjY0NjQ1IDExLjAyMDFMMy42NDY0NSA3LjAyMDA2QzMuNDUxMTggNi44MjQ4IDMuNDUxMTggNi41MDgyMSAzLjY0NjQ1IDYuMzEyOTVDMy44NDE3MSA2LjExNzY5IDQuMTU4MjkgNi4xMTc2OSA0LjM1MzU1IDYuMzEyOTVMOCA5Ljk1OTRMMTEuNjQ2NCA2LjMxMjk1QzExLjg0MTcgNi4xMTc2OSAxMi4xNTgzIDYuMTE3NjkgMTIuMzUzNiA2LjMxMjk1WiIgY2xpcC1ydWxlPSJldmVub2RkIi8+PC9zdmc+');
  background-position: right ${remCalc(12)} top ${remCalc(18)};
  background-repeat: no-repeat;

  width: 100%;
  height: ${remCalc(56)};

  border: 1px solid ${COLORS.BLACK};
  border-radius: ${remCalc(4)};

  ${customTextSize(17, 24)}

  transition: background-color 0.2s ease-in-out,
              border-color 0.2s ease-in-out,
              border-width 0.2s ease-in-out;

  &:focus,
  &:active {
    background-color: ${COLORS.WHITE};
    border-color: ${COLORS.BLUE.BASE};
    border-width: 2px;
    outline: none;
  }
`;

const OpenPositionFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${remCalc(16)};

  margin-bottom: ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
    justify-content: flex-start;
  }

  ${mediaBreakpointUp('desktop')} {
    ${LargeSelectField} {
      flex-basis: ${remCalc(300)};
    }
  }
`;

const OpenPositionText = styled.div`
  ${customTextSize(16, 24)}
  text-align: center;

  ${paddingY(48)}

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(20, 28)}
  }

  p + p {
    margin-top: ${remCalc(16)};
  }

  a {
    text-decoration: underline;
    color: ${COLORS.BLUE.LOWLIGHT};
  }
`;


const BlockQuote = styled.blockquote`
  max-width: ${remCalc(556)};
  > p,
  > footer {
    color: ${COLORS.BLACK};
  }
`;

// #endregion - Components

interface JobsPageProps extends CareersPageQuery {
  jobs: Jobs;
}

const JobsPage: PageTemplate<JobsPageProps> = ({ entry, ...props }) => {
  const [currentBreakpoint] = useBreakpoint();
  const [selectedLocationIndex, setLocationIndex] = useState<number>(0);
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const openPositionRef = useRef<HTMLElement>(null);
  const openRolesFooterRef = useRef<HTMLDivElement>(null);

  // #region - Jobs

  const { data: jobs } = useSWR<Jobs>('/jobs/feed', (url) => axios.get(url).then((res) => res.data), {
    initialData: props.jobs,
    revalidateOnMount: true,
  });

  const jobDepartments = useMemo(() => jobs?.map((department) => department.name).sort() ?? [], [jobs]);

  const jobLocations = useMemo(() => {
    const locations = jobs?.flatMap((department) => department.jobs.map((job) => job.location)) ?? [];

    return [...new Set(locations)].sort();
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];

    let filteredJobs = cloneDeep(jobs);

    if (departmentFilter !== '') {
      filteredJobs = [filteredJobs?.find(({ name }) => name === departmentFilter) as Department];
    }

    if (locationFilter !== '') {
      filteredJobs = filteredJobs?.map((department) => {
        department.jobs = department.jobs.filter((job) => job.location === locationFilter);

        return department;
      });
    }

    filteredJobs = filteredJobs.filter((department) => department.jobs.length > 0);

    return filteredJobs;
  }, [jobs, locationFilter, departmentFilter]);

  // #endregion - Jobs

  const newsCarouselItems =
    entry?.newsBlocks?.map((block, i) => (
      <CarouselItem key={`news-carousel-item-${i}`}>
        {!!block?.supertitle && <CarouselItemSupertitle>{block.supertitle}</CarouselItemSupertitle>}
        <CarouselItemTitle>
          <ConditionalWrapper
            condition={!!block?.linkUrl}
            wrapper={(children) => (
              <a
                href={block?.linkUrl!}
                target="_blank"
                onClick={() => {
                  gtm.track('click', {
                    element_label: maybeNotSet(block?.itemText),
                    element_type: 'link',
                    location: 'News Section',
                  });
                }}
              >
                {children}
              </a>
            )}
          >
            {block?.itemText}
          </ConditionalWrapper>
        </CarouselItemTitle>
      </CarouselItem>
    )) ?? [];

  const benefitsCarouselItems =
    entry?.benefits?.map((benefit, i) => (
      <BenefitCarouselItem key={`benefits-carousel-item-${i}`}>
        <BenefitCarouselItemIcon>{benefit?.glyph && <Glyph name={benefit?.glyph} size={50} />}</BenefitCarouselItemIcon>

        <BenefitCarouselItemTitle>{benefit?.benefitName}</BenefitCarouselItemTitle>

        {!!benefit?.benefitText && (
          <BenefitCarouselItemText>{parseHtml(benefit?.benefitText?.renderHtml)}</BenefitCarouselItemText>
        )}
      </BenefitCarouselItem>
    )) ?? [];

  const locations =
    entry?.locations?.map((location) => {
      const asset = getSingle(location?.locationImage);

      return {
        title: location?.locationName,
        text: parseHtml(location?.locationDescription?.renderHtml),
        image: {
          src: asset?.url!,
          width: asset?.width!,
          height: asset?.height!,
        },
      };
    }) ?? [];

  const quotePhoto = getSingle(entry?.cultureQuoteAuthorPhoto);

  useLayoutEffect(() => {
    if (openRolesFooterRef.current) {
      const handler = (e: MouseEvent) => {
        const label = (e.target as HTMLAnchorElement).textContent;

        gtm.track('click', {
          element_type: 'link',
          location: 'Open Positions Footer',
          element_label: maybeNotSet(label),
        });
      };

      const links = openRolesFooterRef.current.querySelectorAll('a');

      links.forEach((link) => link.addEventListener('click', handler));

      return () => {
        links.forEach((link) => link.removeEventListener('click', handler));
      };
    }
  }, [openRolesFooterRef]);

  return (
    <>
      <Hero>
        <Container>
          <HeroHeadline>{entry?.heroTitle ?? ''}</HeroHeadline>

          {!!entry?.heroText && <HeroText>{parseHtml(entry?.heroText)}</HeroText>}

          <HeroCTA>
            <PrimaryInnerGlowButton
              onClick={() => {
                gtm.track('click', {
                  element_type: 'button',
                  location: 'Hero',
                  element_label: entry?.ctaText ?? 'See our open positions',
                });

                openPositionRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {entry?.ctaText ?? 'See our open positions'}
            </PrimaryInnerGlowButton>
          </HeroCTA>
        </Container>
      </Hero>

      <NewsSection>
        <Container>
          <NewsHeadline>{entry?.newsHeading ?? ''}</NewsHeadline>
          {!!entry?.newsSubhead && <SectionHeadlineText>{parseHtml(entry?.newsSubhead)}</SectionHeadlineText>}

          {currentBreakpoint === 'mobile' ? (
            <StyledCarousel items={newsCarouselItems ?? []} />
          ) : (
            <NewsGrid>{newsCarouselItems?.map((item) => item) ?? []}</NewsGrid>
          )}
        </Container>
      </NewsSection>

      <PhotoGrid>
        {entry?.valuesPhotos?.map((photo, index) => {
          if (!photo) return null;

          return (
            <div key={photo.url ?? index}>
              <Image src={photo.url!} alt={photo.altText ?? ''} fill />
            </div>
          );
        })}
      </PhotoGrid>
      <ValuesSection>
        <Container>
          <SectionHeadline>{entry?.valuesHeading ?? ''}</SectionHeadline>

          <Accordion
            items={
              entry?.values?.map((value) => ({
                title: value?.valueName ?? '',
                content: parseHtml(value?.valueText?.renderHtml ?? ''),
              })) ?? []
            }
            onChangeActiveIndex={(index) => {
              gtm.track('click', {
                element_type: 'button',
                element_label: `Accordion | ${maybeNotSet(entry?.values[index]?.valueName)}`,
                location: 'Company Values',
              });
            }}
          />
        </Container>
      </ValuesSection>

      <BenefitsSection>
        <Container>
          <SectionHeadline>{entry?.benefitsHeading ?? ''}</SectionHeadline>

          {currentBreakpoint === 'mobile' ? (
            <StyledCarousel items={benefitsCarouselItems} />
          ) : (
            <BenefitsGrid>{benefitsCarouselItems.map((item) => item)}</BenefitsGrid>
          )}
        </Container>
      </BenefitsSection>

      <CultureSection>
        <Container>
          <SectionHeadline>{entry?.cultureHeading ?? ''}</SectionHeadline>

          <CulturePhotoGrid>
            {entry?.culturePhotos?.map((item, index) => {
              if (!item || !item.photo) return null;

              const photo = getSingle(item.photo);

              if (!photo) return null;

              return (
                <div key={photo.url ?? index}>
                  <div>
                    <Image src={photo.url!} alt={photo.altText ?? ''} fill />
                  </div>

                  <p>{item.caption ?? ''}</p>
                </div>
              );
            })}
          </CulturePhotoGrid>

          <CultureQuote>
            {!!quotePhoto && (
              <CultureQuoteImageWrapper>
                <CultureQuoteImage
                  src={quotePhoto.url!}
                  width={quotePhoto.width!}
                  height={quotePhoto.height!}
                  alt={quotePhoto.altText ?? ''}
                />
              </CultureQuoteImageWrapper>
            )}

            {!!entry?.cultureQuote && (
              <BlockQuote>
                {parseHtml(entry?.cultureQuote) ?? ''}

                {!!entry.cultureQuoteAuthorName && (
                  <footer>
                    <strong>{entry.cultureQuoteAuthorName},</strong> {entry.cultureQuoteAuthorTitle}
                  </footer>
                )}
              </BlockQuote>
            )}
          </CultureQuote>
        </Container>
      </CultureSection>

      <LocationsSection>
        <Container>
          <SectionHeadline>Our Locations</SectionHeadline>

          <LocationsGrid>
            <LocationImageWrapper>
              {locations[selectedLocationIndex].image && (
                <Image src={locations[selectedLocationIndex].image?.src!} alt="" fill />
              )}
            </LocationImageWrapper>

            <Accordion
              onChangeActiveIndex={(index) => {
                setLocationIndex(index);

                gtm.track('click', {
                  element_type: 'button',
                  location: 'Company Locations',
                  element_label: `Accordion | ${maybeNotSet(locations[index].title)}`,
                });
              }}
              items={locations.map((location) => ({
                title: location.title ?? '',
                content: (
                  <>
                    <LocationsAccordionImage>
                      <Image src={locations[selectedLocationIndex].image?.src!} alt="" fill />
                    </LocationsAccordionImage>
                    {location.text}
                  </>
                ),
              }))}
            />
          </LocationsGrid>
        </Container>
      </LocationsSection>

      <OpenPositionsSection ref={openPositionRef}>
        <Container>
          <SectionHeadline>{entry?.openRolesHeading ?? ''}</SectionHeadline>

          {!!entry?.openRolesSubhead && <SectionHeadlineText>{parseHtml(entry?.openRolesSubhead)}</SectionHeadlineText>}

          <OpenPositionFilters>
            <LargeSelectField
              onChange={(e) => {
                setLocationFilter(e.target.value);

                gtm.track('change_filter', {
                  location: 'Open Positions',
                  element_label: `Location Filter | ${e.target.value ?? 'All Locations'}`,
                });
              }}
            >
              <option value="" selected={locationFilter === ''}>
                All locations
              </option>
              {jobLocations
                .filter((loc) => loc !== '')
                .map((location) => (
                  <option key={location} value={location} selected={locationFilter === location}>
                    {location}
                  </option>
                ))}
            </LargeSelectField>

            <LargeSelectField
              onChange={(e) => {
                setDepartmentFilter(e.target.value);

                gtm.track('change_filter', {
                  element_label: `Department Filter | ${e.target.value ?? 'All Departments'}`,
                  location: 'Open Positions',
                });
              }}
            >
              <option value="" selected={departmentFilter === ''}>
                All departments
              </option>
              {jobDepartments.map((department) => (
                <option key={department} value={department} selected={departmentFilter === department}>
                  {department}
                </option>
              ))}
            </LargeSelectField>
          </OpenPositionFilters>

          {filteredJobs &&
            filteredJobs.map((department) => (
              <OpenPositionGroup key={department.key}>
                <OpenPositionDepartment>{department.name}</OpenPositionDepartment>

                <ul>
                  {department.jobs.map((job) => (
                    <OpenPositionItem key={job.key}>
                      <a
                        href={job.link}
                        onClick={() => {
                          gtm.track('click', {
                            element_type: 'button',
                            location: 'Open Positions',
                            element_label: `${maybeNotSet(department.name)} / ${maybeNotSet(job.title)} / ${maybeNotSet(
                              job.location,
                            )}`,
                          });
                        }}
                      >
                        <p>{job.title}</p>
                        <p>{job.location}</p>
                      </a>
                    </OpenPositionItem>
                  ))}
                </ul>
              </OpenPositionGroup>
            ))}

          {!!entry?.openRolesFooter && (
            <OpenPositionText ref={openRolesFooterRef}>{parseHtml(entry?.openRolesFooter)}</OpenPositionText>
          )}
        </Container>
      </OpenPositionsSection>
    </>
  );
};

JobsPage.template = MasterTemplate;

export default JobsPage;

export const getStaticProps = getStaticPageProps('jobs', async ({ client }) => {
  const { entry } = await client.request<CareersPageQuery>(CareersPageQueryDocument);

  const jobs = await fetchJobPostings();

  return {
    props: {
      entry,
      jobs,
    },
  };
});
